import { useParams } from 'react-router-dom';

/**
 * Which shell a page is being viewed in.
 *
 * The app has two chromes. `/academy/*` is the academy's own portal —
 * PortalLayoutHider strips the Kynaix Sports header, nav and footer so the
 * academy's branding is the only thing on screen. Everything else keeps the
 * Kynaix chrome.
 *
 * Flows that are reachable from both sides — registration today, payment next —
 * have to stay on the side they started from. A parent who begins registering
 * inside an academy portal should never be dropped onto Kynaix Sports chrome
 * half way through the wizard, and a Kynaix-level tournament registration
 * shouldn't suddenly wear an academy's branding. So pages ask this hook where
 * they are and build their links through `path()` rather than hardcoding one
 * of the two prefixes.
 *
 * @param {object} [academy] the loaded academy, if the caller has it — lets us
 *        prefer the readable slug over a UUID once it's known.
 */
export default function useAcademyScope(academy) {
  const { slug, academyId } = useParams();

  // Mounted under /academy/:slug — i.e. inside the portal.
  const inAcademy = !!slug;

  // What the URL should call this academy. The slug reads better and both the
  // academy endpoints and AcademyDetailsPage accept either form.
  const academyKey = slug || academy?.slug || academyId || academy?.id;

  // Program and enrollment endpoints want the real UUID, not a slug, so keep
  // the two identifiers separate rather than hoping one works everywhere.
  const resolvedId = academy?.id || academyId;

  const base = inAcademy ? `/academy/${academyKey}` : `/academies/${resolvedId}`;

  return {
    inAcademy,
    academyKey,
    academyId: resolvedId,

    /** The academy's own page. Always portal-chromed, in either shell. */
    home: (query = '') => `/academy/${academyKey}${query}`,

    /** A page nested under the academy, on whichever side we're already on. */
    path: (suffix) => `${base}/${String(suffix).replace(/^\//, '')}`,
  };
}
